/**
 * Puerto Rico holiday calendar for business operations and scheduling.
 * Used by the barbershop rental application to determine business hours,
 * availability calculations, and administrative office operations specific to Puerto Rico.
 *
 * Based on official Puerto Rico holiday calendar from:
 * https://welcome.topuertorico.org/reference/holi.shtml
 *
 * Each holiday entry contains scheduling metadata:
 * - Fixed dates use month/day properties
 * - Floating holidays use weekday/occurrence properties
 * - Variable dates (Easter-based) have null values and require calculation
 * - Office availability is specified for administrative functions
 *
 * @constant {object} HOLIDAYS_PR - Puerto Rico holiday configuration object
 * @description Holiday calendar for business scheduling and availability calculations in Puerto Rico
 *
 * @property {object} "Año Nuevo" - New Year's Day (January 1), office closed
 * @property {object} "Día de los Reyes Magos" - Three Kings Day (January 6), office closed
 * @property {object} "Día de la Constitución de los Estados Unidos" - US Constitution Day (3rd Monday Feb), office closed
 * @property {object} "Día de la Abolición de la Esclavitud en Puerto Rico" - Abolition of Slavery Day (March 22), office closed
 * @property {object} "Viernes Santo" - Good Friday (variable Easter date), office closed
 * @property {object} "Pascuas" - Easter Sunday (variable date), office open
 * @property {object} "Día del Trabajo" - Labor Day (1st Monday May), office closed
 * @property {object} "Día de la Independencia de Estados Unidos (Independence Day)" - Independence Day (July 4), office closed
 * @property {object} "Día de la Recordación" - Memorial Day Puerto Rico (July 30), office closed
 * @property {object} "Día de la Constitución" - Constitution Day (July 25), office closed
 * @property {object} "Día de la Abolición de la Esclavitud" - Abolition Day (March 22), office closed
 * @property {object} "Día de los Veteranos" - Veterans Day (November 11), office closed
 * @property {object} "Día de Acción de Gracias" - Thanksgiving (4th Thursday Nov), office closed
 * @property {object} "Día de la Constitución de Puerto Rico" - PR Constitution Day (July 25), office closed
 * @property {object} "Descubrimiento de América (Columbus Day)" - Columbus Day (2nd Monday Oct), office open
 * @property {object} "Navidad" - Christmas Day (December 25), office closed
 *
 * Each holiday object structure:
 * @property {number|null} month - Month number (1-12) for fixed dates, null for variable dates
 * @property {number|null} day - Day number for fixed dates, null for floating/variable dates
 * @property {number|null} weekday - Day of week (0=Sunday, 1=Monday, etc.) for floating holidays
 * @property {number|null} occurrence - Week occurrence (1=first, 2=second, 3=third, 4=fourth, -1=last)
 * @property {boolean} officeOpen - Whether administrative office operates on this day
 */
import { HolidayInfo } from '../@models/holiday-info';

export const HOLIDAYS_PR: Record<string, HolidayInfo> = {
  'HOLIDAYS_PR.NEW_YEAR': { month: 1, day: 1, weekday: null, occurrence: null, officeOpen: false },
  'HOLIDAYS_PR.THREE_KINGS_DAY': {
    month: 1,
    day: 6,
    weekday: null,
    occurrence: null,
    officeOpen: false,
  },
  'HOLIDAYS_PR.US_CONSTITUTION_DAY': {
    month: 2,
    day: null,
    weekday: 1,
    occurrence: 3,
    officeOpen: false,
  }, // Third Monday in February
  'HOLIDAYS_PR.ABOLITION_SLAVERY_PR': {
    month: 3,
    day: 22,
    weekday: null,
    occurrence: null,
    officeOpen: false,
  },
  'HOLIDAYS_PR.GOOD_FRIDAY': {
    month: null,
    day: null,
    weekday: null,
    occurrence: null,
    officeOpen: false,
  }, // Variable date
  'HOLIDAYS_PR.EASTER': {
    month: null,
    day: null,
    weekday: null,
    occurrence: null,
    officeOpen: true,
  }, // Variable date
  'HOLIDAYS_PR.LABOR_DAY': { month: 9, day: null, weekday: 1, occurrence: 1, officeOpen: false }, // First Monday in May
  'HOLIDAYS_PR.US_INDEPENDENCE_DAY': {
    month: 7,
    day: 4,
    weekday: null,
    occurrence: null,
    officeOpen: false,
  },
  'HOLIDAYS_PR.MEMORIAL_DAY_PR': {
    month: 7,
    day: 30,
    weekday: null,
    occurrence: null,
    officeOpen: false,
  },
  'HOLIDAYS_PR.PR_CONSTITUTION_DAY': {
    month: 7,
    day: 25,
    weekday: null,
    occurrence: null,
    officeOpen: false,
  },
  'HOLIDAYS_PR.VETERANS_DAY': {
    month: 11,
    day: 11,
    weekday: null,
    occurrence: null,
    officeOpen: false,
  },
  'HOLIDAYS_PR.THANKSGIVING': {
    month: 11,
    day: null,
    weekday: 4,
    occurrence: 4,
    officeOpen: false,
  }, // Fourth Thursday in November
  'HOLIDAYS_PR.COLUMBUS_DAY': { month: 10, day: null, weekday: 1, occurrence: 2, officeOpen: true }, // Second Monday in October
  'HOLIDAYS_PR.CHRISTMAS': {
    month: 12,
    day: 25,
    weekday: null,
    occurrence: null,
    officeOpen: false,
  },
};
// //function to calculate variable date holidays like Good Friday and Easter can be added as needed
// function getVariableDateHolidaysPR(year: number) {
//     const easter = calculateEaster(year);
//     const goodFriday = new Date(easter);
//     goodFriday.setDate(easter.getDate() - 2); // Good Friday is two days before Easter

//     return {
//         "Viernes Santo": { month: goodFriday.getMonth() + 1, day: goodFriday.getDate(), weekday: null, occurrence: null, officeOpen: false },
//         "Pascuas": { month: easter.getMonth() + 1, day: easter.getDate(), weekday: null, occurrence: null, officeOpen: true },
//     };
// }

// // Function to calculate Easter date for a given year
// function calculateEaster(year: number): Date {
//     const f = Math.floor,
//         G = year % 19,
//         C = f(year / 100),
//         H = (C - f(C / 4) - f((8 * C + 13) / 25) + 19 * G + 15) % 30,
//         I = H - f(H / 28) * (1 - f(29 / (H + 1)) * f((21 - G) / 11)),
//         J = (year + f(year / 4) + I + 2 - C + f(C / 4)) % 7,
//         L = I - J,
//         month = 3 + f((L + 40) / 44),
//         day = L + 28 - 31 * f(month / 4);
//     return new Date(year, month - 1, day);
// }
